<template>
  <div class="space-y-16 lg:space-y-24 py-12">
    <TContainer>
      <THeading
        :level="3"
        class="mb-2"
      >
        {{ pageHeading }}
      </THeading>

      <p class="font-semibold">
        {{ t('subtitle') }}
      </p>

      <AppMosaicImages
        v-if="eventMosaicImages?.length"
        :images="eventMosaicImages"
        aspect-ratio="21/9"
        class="mt-6"
      />

      <TBreadcrumb
        v-if="breadcrumbs?.length"
        :items="breadcrumbs"
        class="mt-4 lg:justify-end"
      />
    </TContainer>

    <div v-if="data?.event?.Description">
      <TContainer size="xl">
        <div class="space-y-4">
          <THeading
            v-if="data?.event?.Title"
            :level="5"
            tag="p"
          >
            {{ data.event.Title }}
          </THeading>

          <p>{{ data.event.Description }}</p>
        </div>
      </TContainer>
    </div>

    <TContainer
      v-if="gridRvs?.length"
      size="xl"
      class="space-y-8"
    >
      <THeading
        tag="h2"
        :level="2"
      >
        {{ t('rvsGridTitle', { eventName: data?.event?.Name ?? '' }) }}
      </THeading>

      <Grid
        :max-results="gridRvs?.length ?? pageSize"
        :sm-cols="2"
        :lg-cols="3"
        :rows="6"
        :sm-rows="4"
        :lg-rows="3"
      >
        <template #default="{ getItemClass }">
          <CardRv
            v-for="(rv, index) in gridRvs"
            :key="rv.Id"
            :index="index"
            search-request-id=""
            :rv="rv"
            no-lazy-load
            cta="poi-rvs-grid"
            discovery-source="POI"
            :class="getItemClass(index)"
            is-seo-variant
            @visible:rv="queueViewableImpression"
          />
        </template>
      </Grid>

      <div class="flex justify-center">
        <TButton
          variant="highlight"
          class="w-full sm:w-auto"
          @click="navigateToRvSearch"
        >
          {{ t('browseRVRentals') }}
        </TButton>
      </div>
    </TContainer>

    <ExperimentComponent experiment-key="rvz26351UpdatedBrandDifferentiatorCards">
      <template #enabled>
        <TContainer
          size="xl"
          class="mb-8"
        >
          <GeneralBrandPillars focus="guest" />
        </TContainer>
      </template>
    </ExperimentComponent>

    <TContainer
      v-if="data?.topHosts?.length"
      size="xl"
    >
      <LazyRvRentalsTopHostsSection
        :hosts="data.topHosts"
        :location-title="t('near', { locationTitle })"
      />
    </TContainer>

    <TContainer
      v-if="data?.event?.Overview"
      size="xl"
    >
      <div class="space-y-4">
        <THeading
          v-if="data?.event?.Venue"
          :level="5"
          tag="h2"
        >
          {{ t('rvsCampingNear', { venue: data.event.Venue }) }}
        </THeading>

        <AppCollapsibleContent
          :content="data.event.Overview"
          more-button-variant="link"
          is-show-more-button
          :line-clamp="10"
        />
      </div>
    </TContainer>

    <TContainer
      size="xl"
      class="space-y-16"
    >
      <RvRentalsCampgroundDelivery
        :campgrounds="data?.nearbyCampgrounds?.Data?.map((campground) => ({
          name: campground.Name,
          id: campground.Id,
        })) ?? []"
      />

      <div class="flex justify-center">
        <TButton
          variant="highlight"
          @click="navigateToRvSearch({ Delivery: true })"
        >
          {{ t('viewDeliverableRVs') }}
        </TButton>
      </div>
    </TContainer>

    <div
      v-if="data?.event?.Latitude && data?.event?.Longitude"
      class="space-y-8"
    >
      <TContainer size="xl">
        <THeading
          :level="3"
          tag="h2"
        >
          {{ t('rvRentalsAndCampingNearTitle', { eventName: data?.event?.Name }) }}
        </THeading>
      </TContainer>

      <TContainer size="2xl">
        <RvRentalsMap
          :center="{ lat: data.event.Latitude, lng: data.event.Longitude }"
          :zoom="10"
          :rvs="data?.rvs ?? []"
          :campgrounds="data?.nearbyCampgrounds?.Data ?? []"
          :events="[data.event]"
        />
      </TContainer>

      <TContainer
        size="xl"
        class="space-y-4"
      >
        <p class="text-center font-medium">
          {{ data?.event.Venue }}, {{ locationTitle }}, {{ data?.event.Country }}
        </p>

        <p v-if="data?.event.HowToGetThere">
          {{ data.event.HowToGetThere }}
        </p>
      </TContainer>
    </div>

    <TContainer size="2xl">
      <div class="bg-green-50 max-sm:-mx-4 sm:relative sm:left-1/2 sm:-translate-x-1/2 sm:w-dvw xl:static xl:left-auto xl:translate-x-0 xl:w-auto">
        <TContainer size="xl">
          <RvRentalsRvezyVsOthers class="py-12 md:py-16" />
        </TContainer>
      </div>
    </TContainer>

    <LazyRvRentalsFaqs
      v-if="data?.event?.FAQ?.length"
      :faqs="data?.event?.FAQ?.map((faq) => ({
        Id: faq.Id,
        Question: faq.Question ?? '',
        Answer: faq.Answer ?? '',
        Source: faq.Source ?? undefined,
      })) ?? []"
      :rv-title="t('anRVFor')"
      :location-title="data?.event?.Name ?? ''"
    />
  </div>
</template>

<script setup lang="ts">
import type { Breadcrumb } from '~/components/_tailwind/breadcrumb/types'
import { type COUNTRY_SHORTS, ROUTE_BASE_NAME } from '~/constants'
import { POINT_OF_INTEREST_COUNTRY_ID } from '~/constants/point-of-interest'

definePageMeta({
  layout: 'static',

  i18n: {
    paths: {
      en: '/rv-rentals/event/[eventSlug]',
      fr: '/locations-vr/evenement/[eventSlug]',
    },
  },
})

const route = useRoute()
const localePath = useLocalePath()
const { locale, t } = useI18n()

const eventSlug = computed(() => Array.isArray(route.params.eventSlug) ? route.params.eventSlug.join(' ') : route.params.eventSlug)

const { $rentals, $gis } = useNuxtApp()
const { getPOIs } = usePointOfInterest()
const { navigateToRvSearch } = useSearch()
const searchData = useSearchData()

const { data } = await useAsyncData(`rv-rentals-event-${eventSlug.value}-${locale.value}`, async () => {
  if (!eventSlug.value) {
    return { event: undefined }
  }

  const event = await $rentals('/api/events/{eventSlug}', {
    path: {
      eventSlug: eventSlug.value,
    },
    query: {
      languageCode: locale.value,
    },
  })

  if (!event) {
    return { event }
  }

  const [gisData, nearbyLocations, nearbyRVs, topHosts, nearbyCampgrounds] = await Promise.all([
    $gis('/Geocoding/Geocode', {
      query: {
        Address: [event.City, event.Region, event.Country].filter(Boolean).join(', '),
        Language: locale.value,
      },
    }),

    getPOIs({
      Latitude: event.Latitude,
      Longitude: event.Longitude,
      PageSize: 12,
      PointOfInterestTypeName: 'City',
      DistanceKM: 250,
      ParentPointOfInterestId: POINT_OF_INTEREST_COUNTRY_ID[event.Country as COUNTRY_SHORTS],
    }),

    $rentals('/api/rvlistings/unified-search', {
      query: {
        SearchLat: event.Latitude,
        SearchLng: event.Longitude,
        Country: event.Country,
        PageSize: 50,
        SearchDistance: 200,
        IncludeFeatured: false,
        SortOrder: 'DistanceASC',
        FestivalFriendly: true,
      },
    }),

    $rentals('/api/hosts/top-hosts', {
      query: {
        Latitude: event.Latitude,
        Longitude: event.Longitude,
        RadiusKm: 50,
        Limit: 2,
      },
    }),

    $rentals('/api/campgrounds', {
      query: {
        PageSize: 20,
        Latitude: event.Latitude,
        Longitude: event.Longitude,
        Country: event.Country as COUNTRY_SHORTS,
        Language: lookupLocaleToLanguage(locale.value),
        DistanceKm: 100,
        SortBy: 'DistanceAsc',
      },
    }),
  ])

  return {
    event,
    gisData,
    nearbyLocations,
    rvs: nearbyRVs?.PopularRVs?.ListRVs ?? [],
    topHosts,
    nearbyCampgrounds,
  }
})

if (!data.value?.event) {
  throw createError({
    status: 404,
    statusText: 'Event not found',
    fatal: true,
  })
}

const { prePopulateSearchLocation } = useLocationPredictions()
const { updateSearchFormLocation } = useSearchForm()

onMounted(() => {
  const placeId = data.value?.gisData?.placeId
  const locationText = data.value?.gisData?.city ?? data.value?.gisData?.name ?? ''

  prePopulateSearchLocation(locationText)

  if (placeId) {
    updateSearchFormLocation({
      placeId,
      text: locationText,
    })
    searchData.value.location = data.value?.gisData
  }
})

function i18nParams(lang: SupportedLocales) {
  if (locale.value === lang) {
    return { eventSlug: eventSlug.value }
  }

  const alternate = data.value?.event?.AlternateSlug
  if (alternate) {
    return { eventSlug: alternate }
  }

  return false
}

const setI18nParams = useSetI18nParams()
setI18nParams({
  en: i18nParams('en'),
  fr: i18nParams('fr'),
})

// Clear localized dynamic params before global i18n middleware runs; otherwise
// switchLocalePath merges stale eventSlug into unrelated routes (e.g. index) and Vue Router warns.
onBeforeRouteLeave(() => {
  setI18nParams({})
})

const { getStaticImage, presets } = useImage()

const eventMosaicImages = computed(() => {
  if (!data.value?.event?.Images?.length) {
    return []
  }

  const preset = presets.LANDSCAPE_LG

  return data.value.event.Images.map((image) => {
    const url = getStaticImage({
      path: image.Path,
      preset,
    }) ?? ''

    return {
      type: 'image' as const,
      path: image.Path,
      src: url,
      isStatic: true,
      description: image.AltText ?? data.value?.event?.Name ?? '',
      width: preset.width,
      height: preset.height,
    }
  })
})

useSeoHeadTags({
  title: computed(() => data.value?.event?.MetaTitle ?? ''),
  description: computed(() => data.value?.event?.MetaDescription ?? ''),
  image: computed(() => data.value?.event?.MetaImage ? getStaticImage({ path: data.value?.event?.MetaImage, preset: presets.LANDSCAPE_LG }) : undefined),
})

const locationTitle = computed(() =>
  [data.value?.gisData?.city, data.value?.gisData?.regionCode].filter(Boolean).join(', '),
)

const pageHeading = computed(() => {
  const eventName = data.value?.event?.Name ? t('rvRentalsForTitle', { eventName: data.value?.event?.Name }) : ''
  const location = locationTitle.value
  if (!location) {
    return eventName
  }
  return `${eventName} - ${location}`
})

const hasRelatedPOICity = computed(() => data.value?.nearbyLocations?.some((location) => location.Name === data.value?.event?.City))

const breadcrumbs = computed(() => {
  const items: Breadcrumb[] = [
    {
      text: t('pageLink.rvRentals'),
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals,
      }),
    }]

  const gisData = data.value?.gisData
  const pathSegments: { text: string, slug: string }[] = []

  if (gisData?.country) {
    pathSegments.push({ text: gisData.country, slug: formatAsSlug(gisData.country) })
  }
  if (gisData?.region) {
    pathSegments.push({ text: gisData.region, slug: formatAsSlug(gisData.region) })
  }
  if (gisData?.city && hasRelatedPOICity.value) {
    pathSegments.push({ text: gisData.city, slug: formatAsSlug(gisData.city) })
  }

  const destinationPath: string[] = []
  for (const segment of pathSegments) {
    destinationPath.push(segment.slug)
    items.push({
      text: segment.text,
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals_destination,
        params: { keyword: undefined, destinationPath: [...destinationPath] },
      }),
    })
  }

  items.push({
    text: data.value?.event?.Name ?? '',
    to: localePath({
      name: ROUTE_BASE_NAME.rv_rentals_event,
      params: {
        eventSlug: eventSlug.value,
      },
    }),
  })

  return items
})

const pageSize = 9
const gridRvs = data.value?.rvs?.slice(0, pageSize) ?? []

const { queueViewableImpression } = useRvViewableImpressions({
  pageNumber: 1,
  pageSize,
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        text: t('pageLink.home'),
        to: localePath({ name: 'index' }),
      },
      ...breadcrumbs.value,
    ].map((breadcrumb) => ({
      name: breadcrumb.text,
      item: breadcrumb.to,
    })),
  }),
])
</script>

<i18n lang="json">
{
  "en": {
    "subtitle": "Discover the RVezy RV rental marketplace",
    "pageTitle": "RV rentals for {eventName} — {locationTitle}",
    "rvsGridTitle": "Featured RV rentals for {eventName}",
    "rvRentalsForTitle": "RV rentals for {eventName}",
    "rvRentalsAndCampingNearTitle": "RV rentals and campgrounds for {eventName}",
    "browseRVRentals": "Browse RV rentals",
    "viewDeliverableRVs": "View deliverable RVs",
    "rvsCampingNear": "RV camping near {venue}",
    "anRVFor": "an RV for",
    "near": "near {locationTitle}"
  },
  "fr": {
    "subtitle": "Découvrez la plateforme de location de VRs entre particuliers RVezy",
    "pageTitle": "Locations de VR pour {eventName} — {locationTitle}",
    "rvsGridTitle": "Locations de VR en vedette pour {eventName}",
    "rvRentalsForTitle": "VRs en location pour {eventName}",
    "rvRentalsAndCampingNearTitle": "VRs à louer et camping pour {eventName}",
    "browseRVRentals": "Parcourir les VRs",
    "viewDeliverableRVs": "Voir les VRs livrables",
    "rvsCampingNear": "Camping en VR à proximité de {venue}",
    "anRVFor": "un VR pour",
    "near": "près de {locationTitle}"
  }
}
</i18n>
